import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert, Button, Table, ProgressBar, Tabs, Tab, Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import PainelGoogleAds from '../../components/GoogleAds/PainelGoogleAds';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
    fetchAnalyticsRealtime,
    fetchAnalyticsStatus,
    fetchAnalyticsOverview,
    fetchAnalyticsTopProducts,
    fetchAnalyticsTopPages,
    fetchProdutoByReferencia
} from '../../services/api';

// Coordenadas das principais cidades brasileiras
const cityCoordinates = {
    'São Paulo': { lat: -23.55, lng: -46.63 },
    'Rio de Janeiro': { lat: -22.91, lng: -43.17 },
    'Belo Horizonte': { lat: -19.92, lng: -43.94 },
    'Brasília': { lat: -15.79, lng: -47.88 },
    'Salvador': { lat: -12.97, lng: -38.50 },
    'Fortaleza': { lat: -3.72, lng: -38.54 },
    'Curitiba': { lat: -25.43, lng: -49.27 },
    'Recife': { lat: -8.05, lng: -34.88 },
    'Porto Alegre': { lat: -30.03, lng: -51.23 },
    'Manaus': { lat: -3.12, lng: -60.02 },
    'Goiânia': { lat: -16.68, lng: -49.25 },
    'Campinas': { lat: -22.91, lng: -47.06 },
    'Limeira': { lat: -22.56, lng: -47.40 },
    'Piracicaba': { lat: -22.73, lng: -47.65 },
    'Ribeirão Preto': { lat: -21.18, lng: -47.81 },
    'Santos': { lat: -23.96, lng: -46.33 },
    'Niterói': { lat: -22.88, lng: -43.10 },
    'Natal': { lat: -5.79, lng: -35.21 },
    'João Pessoa': { lat: -7.12, lng: -34.86 },
    'Campo Grande': { lat: -20.47, lng: -54.62 },
    'Cuiabá': { lat: -15.60, lng: -56.10 },
    'Florianópolis': { lat: -27.59, lng: -48.55 },
    'Vitória': { lat: -20.32, lng: -40.34 },
    'Belém': { lat: -1.46, lng: -48.50 },
    'São Luís': { lat: -2.53, lng: -44.27 },
    'Maceió': { lat: -9.67, lng: -35.74 },
    'Teresina': { lat: -5.09, lng: -42.80 },
    'Aracaju': { lat: -10.91, lng: -37.07 },
    'Londrina': { lat: -23.30, lng: -51.18 },
    'Joinville': { lat: -26.30, lng: -48.84 },
    'Americana': { lat: -22.74, lng: -47.33 },
    'Sorocaba': { lat: -23.50, lng: -47.46 },
    'Jundiaí': { lat: -23.19, lng: -46.88 },
    'São Carlos': { lat: -22.01, lng: -47.89 },
    'Bauru': { lat: -22.33, lng: -49.07 },
    'São José dos Campos': { lat: -23.18, lng: -45.88 },
    'Uberlândia': { lat: -18.92, lng: -48.28 },
    'Contagem': { lat: -19.93, lng: -44.05 },
    'Osasco': { lat: -23.53, lng: -46.79 },
    'Guarulhos': { lat: -23.46, lng: -46.53 },
    'Santo André': { lat: -23.66, lng: -46.54 },
    'São Bernardo do Campo': { lat: -23.69, lng: -46.56 },
    'Duque de Caxias': { lat: -22.79, lng: -43.31 },
    'Nova Iguaçu': { lat: -22.76, lng: -43.45 },
    'São Gonçalo': { lat: -22.83, lng: -43.07 },
    'Sao Paulo': { lat: -23.55, lng: -46.63 },
    'Uberlandia': { lat: -18.92, lng: -48.28 }
};

// Tradução de eventos GA4 para português
const eventTranslations = {
    'page_view': 'Visualização de Página',
    'session_start': 'Início de Sessão',
    'first_visit': 'Primeira Visita',
    'user_engagement': 'Engajamento do Usuário',
    'scroll': 'Rolagem de Página',
    'click': 'Clique',
    'view_item': 'Visualizou Produto',
    'view_item_list': 'Visualizou Lista de Produtos',
    'select_item': 'Selecionou Produto',
    'add_to_cart': 'Adicionou ao Carrinho',
    'remove_from_cart': 'Removeu do Carrinho',
    'begin_checkout': 'Iniciou Checkout',
    'add_payment_info': 'Adicionou Info de Pagamento',
    'add_shipping_info': 'Adicionou Info de Envio',
    'purchase': 'Compra Realizada',
    'refund': 'Reembolso',
    'view_promotion': 'Visualizou Promoção',
    'select_promotion': 'Selecionou Promoção',
    'search': 'Pesquisa',
    'view_search_results': 'Visualizou Resultados',
    'login': 'Login',
    'sign_up': 'Cadastro',
    'share': 'Compartilhamento',
    'file_download': 'Download de Arquivo'
};

// Ícones para eventos
const eventIcons = {
    'page_view': '👁️',
    'session_start': '🚀',
    'first_visit': '👋',
    'user_engagement': '💡',
    'scroll': '📜',
    'click': '👆',
    'view_item': '👀',
    'view_item_list': '📋',
    'select_item': '✅',
    'add_to_cart': '🛒',
    'remove_from_cart': '❌',
    'begin_checkout': '🛍️',
    'add_payment_info': '💳',
    'add_shipping_info': '📦',
    'purchase': '💰',
    'refund': '↩️',
    'view_promotion': '🏷️',
    'select_promotion': '🎯',
    'search': '🔍',
    'view_search_results': '📄',
    'login': '🔐',
    'sign_up': '📝',
    'share': '📤',
    'file_download': '⬇️'
};

// Cores para eventos
const eventColors = {
    'purchase': 'success',
    'add_to_cart': 'warning',
    'begin_checkout': 'info',
    'view_item': 'primary',
    'page_view': 'secondary',
    'session_start': 'dark',
    'first_visit': 'info'
};

// Função para traduzir evento
const translateEvent = (eventName) => eventTranslations[eventName] || eventName;
const getEventIcon = (eventName) => eventIcons[eventName] || '📌';
const getEventColor = (eventName) => eventColors[eventName] || 'secondary';

// Função para verificar se cidade é válida
const isValidCity = (city) => {
    if (!city) return false;
    const invalid = ['(not set)', '(other)', '', ' ', 'not set', 'other', '(not provided)'];
    return !invalid.includes(city.toLowerCase().trim());
};

// Formatar duração em minutos e segundos
const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
};

// Formatar valor em reais
const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value || 0);
};

// Função para extrair código do produto da URL
const extractProductCode = (path) => {
    if (!path) return null;
    // Padrões comuns de URL de produto
    // Ex: /pulseira-de-aco-inox-1234 ou /produto/1234 ou /1234-produto-nome
    const patterns = [
        /\/(\d{3,7})(?:\/|$|\?)/,  // /12345 ou /12345/
        /-(\d{3,7})(?:\/|$|\?)/,   // nome-12345
        /\/(\d{3,7})-/,             // /12345-nome
        /produto[\/\-](\d{3,7})/i,  // produto/12345 ou produto-12345
    ];

    for (const pattern of patterns) {
        const match = path.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }
    return null;
};

// Componente de Tooltip com imagem do produto
const ProductImageTooltip = ({ path, title, children }) => {
    const [productImage, setProductImage] = useState(null);
    const [productName, setProductName] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [show, setShow] = useState(false);

    const productCode = extractProductCode(path);

    const loadImage = async () => {
        if (loaded || loading) return;

        setLoading(true);
        try {
            let result = null;

            // Primeiro tenta por código se existir
            if (productCode) {
                result = await fetchProdutoByReferencia(productCode);
                if (result?.produto?.imagemUrl) {
                    setProductImage(result.produto.imagemUrl);
                    setProductName(result.produto.descricao);
                    setLoading(false);
                    setLoaded(true);
                    return;
                }
            }

            // Tenta buscar por nome usando o título da página
            if (title) {
                const response = await fetch(`/api/consulta/produto-por-nome/${encodeURIComponent(title)}`);
                result = await response.json();
                if (result?.success && result?.imagem) {
                    setProductImage(result.imagem);
                    setProductName(result.descricao);
                }
            }
        } catch (err) {
            console.error('Erro ao buscar imagem do produto:', err);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    };

    const handleMouseEnter = () => {
        setShow(true);
        loadImage();
    };

    const handleMouseLeave = () => {
        setShow(false);
    };

    const tooltipContent = (
        <Tooltip id={`product-tooltip-${productCode || title?.substring(0, 20)?.replace(/\s/g, '-')}`} style={{ maxWidth: '280px', zIndex: 9999 }}>
            <div className="text-center p-2">
                {loading ? (
                    <div className="py-3">
                        <Spinner animation="border" size="sm" variant="light" />
                        <div className="mt-2">
                            <small>Buscando produto...</small>
                        </div>
                    </div>
                ) : productImage ? (
                    <div>
                        <img
                            src={productImage}
                            alt={productName || title}
                            style={{
                                width: '220px',
                                height: '220px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid rgba(255,255,255,0.3)',
                                display: 'block',
                                margin: '0 auto 8px'
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="fw-bold" style={{ fontSize: '12px' }}>{productName || title}</div>
                    </div>
                ) : loaded ? (
                    <div className="py-2">
                        <small className="text-muted">
                            📄 {title}
                        </small>
                    </div>
                ) : null}
            </div>
        </Tooltip>
    );

    return (
        <OverlayTrigger
            placement="right"
            show={show}
            overlay={tooltipContent}
        >
            <span
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: 'pointer', display: 'inline-block' }}
            >
                {children}
            </span>
        </OverlayTrigger>
    );
};


const AnalyticsDashboard = () => {
    const [status, setStatus] = useState(null);
    const [realtimeData, setRealtimeData] = useState(null);
    const [overviewData, setOverviewData] = useState(null);
    const [topProducts, setTopProducts] = useState(null);
    const [topPages, setTopPages] = useState(null);
    const [categoryStats, setCategoryStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [mapReady, setMapReady] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(7);
    const [activeTab, setActiveTab] = useState('pages');
    const [expandedCategory, setExpandedCategory] = useState(null);

    const intervalRef = useRef(null);
    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const markersRef = useRef([]);

    // Carregar dados em tempo real
    const loadRealtimeData = useCallback(async () => {
        try {
            setError(null);
            const result = await fetchAnalyticsRealtime();
            if (result.success) {
                setRealtimeData(result.data);
                setLastUpdate(new Date());
            } else {
                setError(result.error || 'Erro ao buscar dados em tempo real');
            }
        } catch (err) {
            setError(err.message);
        }
    }, []);

    // Carregar visão geral
    const loadOverviewData = useCallback(async () => {
        try {
            const result = await fetchAnalyticsOverview(selectedPeriod);
            if (result.success) {
                setOverviewData(result.data);
            }
        } catch (err) {
            console.error('Erro ao carregar overview:', err);
        }
    }, [selectedPeriod]);

    // Carregar produtos mais vistos
    const loadTopProducts = useCallback(async () => {
        try {
            const result = await fetchAnalyticsTopProducts(selectedPeriod, 30);
            if (result.success) {
                setTopProducts(result.data);
            }
        } catch (err) {
            console.error('Erro ao carregar top products:', err);
        }
    }, [selectedPeriod]);

    // Carregar páginas mais visitadas
    const loadTopPages = useCallback(async () => {
        try {
            const result = await fetchAnalyticsTopPages(selectedPeriod, 100);
            if (result.success) {
                setTopPages(result.data || []);
                setCategoryStats(result.categoryStats || []);
            }
        } catch (err) {
            console.error('Erro ao carregar top pages:', err);
        }
    }, [selectedPeriod]);

    // Verificar status
    const checkStatus = useCallback(async () => {
        try {
            const statusResult = await fetchAnalyticsStatus();
            setStatus(statusResult);
            return statusResult.initialized;
        } catch (err) {
            return false;
        }
    }, []);

    // Inicialização
    useEffect(() => {
        const init = async () => {
            const isReady = await checkStatus();
            if (isReady) {
                await Promise.all([
                    loadRealtimeData(),
                    loadOverviewData(),
                    loadTopProducts(),
                    loadTopPages()
                ]);
            }
            setLoading(false);
        };
        init();
    }, [checkStatus, loadRealtimeData, loadOverviewData, loadTopProducts, loadTopPages]);

    // Recarregar quando período mudar
    useEffect(() => {
        if (!loading && status?.initialized) {
            loadOverviewData();
            loadTopProducts();
            loadTopPages();
        }
    }, [selectedPeriod]);

    // Inicializar mapa
    useEffect(() => {
        if (loading || activeTab !== 'realtime') return;

        const timer = setTimeout(() => {
            if (mapContainerRef.current && !mapRef.current) {
                try {
                    mapRef.current = L.map(mapContainerRef.current, {
                        center: [-15.79, -47.88],
                        zoom: 4,
                        zoomControl: true,
                        scrollWheelZoom: true
                    });

                    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                        attribution: '© OpenStreetMap',
                        maxZoom: 18
                    }).addTo(mapRef.current);

                    setMapReady(true);

                    setTimeout(() => {
                        if (mapRef.current) {
                            mapRef.current.invalidateSize();
                        }
                    }, 100);
                } catch (err) {
                    console.error('Erro ao inicializar mapa:', err);
                }
            }
        }, 200);

        return () => {
            clearTimeout(timer);
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                setMapReady(false);
            }
        };
    }, [loading, activeTab]);

    // Atualizar marcadores do mapa
    useEffect(() => {
        if (!mapRef.current || !mapReady || !realtimeData?.locations) return;

        markersRef.current.forEach(marker => {
            try { marker.remove(); } catch (e) { }
        });
        markersRef.current = [];

        const validLocations = [];
        realtimeData.locations.forEach(loc => {
            const coords = cityCoordinates[loc.city];
            if (coords) {
                const radius = Math.min(25, Math.max(8, loc.users * 4));
                const circle = L.circleMarker([coords.lat, coords.lng], {
                    radius: radius,
                    fillColor: '#007bff',
                    fillOpacity: 0.7,
                    color: '#0056b3',
                    weight: 2
                }).addTo(mapRef.current);

                circle.bindPopup(`
                    <div style="text-align: center; min-width: 120px;">
                        <strong style="font-size: 14px;">${loc.city}</strong>
                        <br/>
                        <span style="color: #666; font-size: 12px;">${loc.country}</span>
                        <hr style="margin: 8px 0; border-color: #eee;" />
                        <span style="background: #007bff; color: white; padding: 4px 12px; border-radius: 4px; font-size: 13px;">
                            ${loc.users} usuário${loc.users > 1 ? 's' : ''} ativo${loc.users > 1 ? 's' : ''}
                        </span>
                    </div>
                `);

                markersRef.current.push(circle);
                validLocations.push([coords.lat, coords.lng]);
            }
        });

        if (validLocations.length > 0 && mapRef.current) {
            try {
                if (validLocations.length === 1) {
                    mapRef.current.setView(validLocations[0], 10);
                } else {
                    const bounds = L.latLngBounds(validLocations);
                    mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 8 });
                }
            } catch (e) {
                console.error('Erro ao ajustar bounds:', e);
            }
        }
    }, [realtimeData?.locations, mapReady]);

    // Auto-refresh a cada 30 segundos
    useEffect(() => {
        if (!isPaused && status?.initialized && activeTab === 'realtime') {
            intervalRef.current = setInterval(loadRealtimeData, 30000);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPaused, status, loadRealtimeData, activeTab]);

    const togglePause = () => setIsPaused(!isPaused);

    const refreshAll = async () => {
        setLoading(true);
        await Promise.all([
            loadRealtimeData(),
            loadOverviewData(),
            loadTopProducts(),
            loadTopPages()
        ]);
        setLoading(false);
    };

    // GA4 desligado NÃO pode bloquear a página inteira: o painel do Google Ads
    // tem fonte própria (script do Ads → /api/ads) e funciona sem o Analytics.
    // Antes daqui saía um `return` com o aviso e nada mais renderizava — o que
    // deixaria a tela de campanhas invisível enquanto o GA4 estivesse sem
    // configurar (que é o estado atual: falta GA4_PROPERTY_ID no .env).
    // GA4 desligado NÃO bloqueia a página: o painel do Google Ads tem fonte própria
    // (script do Ads → /api/ads) e funciona sem o Analytics. Antes daqui saía um
    // `return` com um aviso amarelo ocupando o topo — removido a pedido do dono:
    // as seções de GA4 simplesmente não aparecem enquanto a integração estiver sem
    // configurar (falta GA4_PROPERTY_ID no .env), e o que tem dado fica visível.
    if (!loading && status && !status.initialized) {
        return (
            <div className="p-3">
                <PainelGoogleAds />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Carregando dados do Google Analytics...</p>
            </div>
        );
    }

    // Calcular total de visualizações
    const totalViews = topPages?.reduce((sum, p) => sum + (p.views || 0), 0) || 0;

    return (
        <div className="p-3">
            {/* Header */}
            <Row className="align-items-center mb-4">
                <Col md={6}>
                    <h4 className="mb-0">
                        📊 Analytics Dashboard
                    </h4>
                    <small className="text-muted">
                        Dados do Google Analytics 4 - lojagringao.com.br
                    </small>
                </Col>
                <Col md={6} className="text-end">
                    <div className="d-flex align-items-center justify-content-end gap-2">
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
                        >
                            <option value={7}>Últimos 7 dias</option>
                            <option value={14}>Últimos 14 dias</option>
                            <option value={30}>Últimos 30 dias</option>
                            <option value={60}>Últimos 60 dias</option>
                            <option value={90}>Últimos 90 dias</option>
                        </select>
                        <Badge bg={isPaused ? 'warning' : 'success'} className="py-2 px-3">
                            {isPaused ? '⏸️ Pausado' : '🟢 Ao Vivo'}
                        </Badge>
                        <Button
                            variant={isPaused ? 'success' : 'outline-warning'}
                            size="sm"
                            onClick={togglePause}
                        >
                            {isPaused ? '▶️' : '⏸️'}
                        </Button>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={refreshAll}
                            disabled={loading}
                        >
                            🔄 Atualizar
                        </Button>
                    </div>
                    {lastUpdate && (
                        <div className="mt-1">
                            <small className="text-muted">
                                Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')}
                            </small>
                        </div>
                    )}
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" className="mb-3">{error}</Alert>
            )}

            {/* Cards de Resumo - Visão Geral */}
            <Row className="g-3 mb-4">
                <Col md={2}>
                    <Card className="border-0 shadow-sm text-center h-100 bg-primary text-white">
                        <Card.Body className="py-3">
                            <h6 className="mb-1 opacity-75 small">👥 Usuários Agora</h6>
                            <h2 className="fw-bold mb-0">
                                {realtimeData?.activeUsers || 0}
                            </h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm text-center h-100">
                        <Card.Body className="py-3">
                            <h6 className="text-muted mb-1 small">📈 Sessões ({selectedPeriod}d)</h6>
                            <h3 className="fw-bold text-info mb-0">
                                {overviewData?.sessions?.toLocaleString() || 0}
                            </h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm text-center h-100">
                        <Card.Body className="py-3">
                            <h6 className="text-muted mb-1 small">👁️ Visualizações</h6>
                            <h3 className="fw-bold text-secondary mb-0">
                                {overviewData?.pageViews?.toLocaleString() || 0}
                            </h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm text-center h-100">
                        <Card.Body className="py-3">
                            <h6 className="text-muted mb-1 small">🛒 Compras</h6>
                            <h3 className="fw-bold text-success mb-0">
                                {overviewData?.purchases || 0}
                            </h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm text-center h-100">
                        <Card.Body className="py-3">
                            <h6 className="text-muted mb-1 small">💰 Receita</h6>
                            <h4 className="fw-bold text-success mb-0">
                                {formatCurrency(overviewData?.revenue)}
                            </h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm text-center h-100">
                        <Card.Body className="py-3">
                            <h6 className="text-muted mb-1 small">⏱️ Tempo Médio</h6>
                            <h4 className="fw-bold text-warning mb-0">
                                {formatDuration(overviewData?.avgSessionDuration)}
                            </h4>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Tabs */}
            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
            >
                {/* TAB 1: PÁGINAS MAIS VISITADAS */}
                <Tab eventKey="pages" title="📄 Páginas Mais Visitadas">
                    <Row className="g-4 mt-2">
                        {/* Ranking por Categoria */}
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white py-3">
                                    <h6 className="mb-0">📁 Por Categoria ({selectedPeriod} dias)</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {categoryStats?.length > 0 ? (
                                        <Accordion activeKey={expandedCategory}>
                                            {categoryStats.map((cat, idx) => (
                                                <Accordion.Item eventKey={idx.toString()} key={idx}>
                                                    <Accordion.Header
                                                        onClick={() => setExpandedCategory(expandedCategory === idx.toString() ? null : idx.toString())}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                                            <span>
                                                                <span className="me-2">{cat.icon}</span>
                                                                <strong>{cat.name}</strong>
                                                            </span>
                                                            <div>
                                                                <Badge bg="primary" className="me-2">
                                                                    {cat.views?.toLocaleString()} views
                                                                </Badge>
                                                                <Badge bg="secondary">
                                                                    {cat.users?.toLocaleString()} usuários
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="p-0">
                                                        <Table hover size="sm" className="mb-0">
                                                            <tbody>
                                                                {cat.pages?.slice(0, 10).map((page, pidx) => (
                                                                    <tr key={pidx}>
                                                                        <td className="ps-3 py-2">
                                                                            <ProductImageTooltip path={page.path} title={page.title || page.path}>
                                                                                <small style={{ wordBreak: 'break-word', lineHeight: '1.4' }}>
                                                                                    {page.title || page.path}
                                                                                </small>
                                                                            </ProductImageTooltip>
                                                                        </td>
                                                                        <td className="text-end pe-3 py-2" style={{ whiteSpace: 'nowrap' }}>
                                                                            <Badge bg="light" text="dark">
                                                                                {page.views?.toLocaleString()}
                                                                            </Badge>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            ))}
                                        </Accordion>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <div className="fs-1 mb-2">📁</div>
                                            Nenhum dado de categoria disponível
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Lista Completa de Páginas */}
                        <Col md={8}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">🔥 Top 50 Páginas Mais Acessadas ({selectedPeriod} dias)</h6>
                                    <Badge bg="info">{topPages?.length || 0} páginas • {totalViews.toLocaleString()} views</Badge>
                                </Card.Header>
                                <Card.Body className="p-0" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    {topPages?.length > 0 ? (
                                        <Table hover className="mb-0">
                                            <thead className="bg-light sticky-top">
                                                <tr>
                                                    <th className="ps-3" style={{ width: '50px' }}>#</th>
                                                    <th>Página</th>
                                                    <th className="text-center">Categoria</th>
                                                    <th className="text-center">Views</th>
                                                    <th className="text-center">Usuários</th>
                                                    <th className="text-center">Tempo Médio</th>
                                                    <th className="text-center">Rejeição</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {topPages.slice(0, 50).map((page, idx) => (
                                                    <tr key={idx}>
                                                        <td className="ps-3">
                                                            <Badge
                                                                bg={idx < 3 ? 'warning' : idx < 10 ? 'info' : 'secondary'}
                                                                className={idx < 3 ? 'text-dark' : ''}
                                                            >
                                                                {idx + 1}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <ProductImageTooltip path={page.path} title={page.title}>
                                                                <div style={{ cursor: 'pointer' }}>
                                                                    <strong className="d-block text-truncate" style={{ maxWidth: '250px' }}>
                                                                        {page.title || 'Sem título'}
                                                                    </strong>
                                                                    <small className="text-muted text-truncate d-block" style={{ maxWidth: '250px' }}>
                                                                        {page.path}
                                                                    </small>
                                                                </div>
                                                            </ProductImageTooltip>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge
                                                                style={{
                                                                    backgroundColor: page.categoryColor + '20',
                                                                    color: page.categoryColor,
                                                                    border: `1px solid ${page.categoryColor}`
                                                                }}
                                                            >
                                                                {page.categoryIcon} {page.category}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <strong>{page.views?.toLocaleString()}</strong>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="secondary">{page.users?.toLocaleString()}</Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={page.avgDuration > 60 ? 'text-success' : 'text-muted'}>
                                                                {formatDuration(page.avgDuration)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={
                                                                page.bounceRate > 70 ? 'text-danger' :
                                                                    page.bounceRate > 50 ? 'text-warning' : 'text-success'
                                                            }>
                                                                {page.bounceRate?.toFixed(0)}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <div className="fs-1 mb-2">📄</div>
                                            Nenhum dado de página disponível
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                {/* TAB 2: TEMPO REAL */}
                <Tab eventKey="realtime" title="⚡ Tempo Real">
                    <Row className="g-4 mt-2">
                        {/* Mapa */}
                        <Col md={7}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">📍 Localização dos Usuários</h6>
                                    <small className="text-muted">Scroll para zoom • Arraste para mover</small>
                                </Card.Header>
                                <Card.Body className="p-0" style={{ position: 'relative' }}>
                                    <div
                                        ref={mapContainerRef}
                                        style={{
                                            height: '400px',
                                            width: '100%',
                                            borderRadius: '0 0 8px 8px',
                                            background: '#e8e8e8'
                                        }}
                                    />
                                    {!mapReady && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0, left: 0, right: 0, bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'rgba(255,255,255,0.8)'
                                        }}>
                                            <Spinner animation="border" variant="primary" />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Lista de cidades */}
                        <Col md={5}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white py-3">
                                    <h6 className="mb-0">🏙️ Top Cidades</h6>
                                </Card.Header>
                                <Card.Body className="p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {realtimeData?.locations?.filter(loc => isValidCity(loc.city)).length > 0 ? (
                                        <Table hover className="mb-0">
                                            <thead className="bg-light sticky-top">
                                                <tr>
                                                    <th className="ps-3">Cidade</th>
                                                    <th className="text-center" style={{ width: '80px' }}>Ativos</th>
                                                    <th style={{ width: '35%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {realtimeData.locations.filter(loc => isValidCity(loc.city)).slice(0, 12).map((loc, idx) => (
                                                    <tr key={idx}>
                                                        <td className="ps-3">
                                                            <strong>{loc.city}</strong>
                                                            <br />
                                                            <small className="text-muted">{loc.country}</small>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="primary" className="fs-6">{loc.users}</Badge>
                                                        </td>
                                                        <td>
                                                            <ProgressBar
                                                                now={(loc.users / (realtimeData.activeUsers || 1)) * 100}
                                                                variant="info"
                                                                style={{ height: '8px' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <div className="fs-1 mb-2">📍</div>
                                            Nenhum usuário ativo no momento
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Páginas e Eventos em tempo real */}
                    <Row className="g-4 mt-2">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white py-3">
                                    <h6 className="mb-0">📄 Páginas Sendo Acessadas Agora</h6>
                                </Card.Header>
                                <Card.Body className="p-0" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                    {realtimeData?.pages?.length > 0 ? (
                                        <Table hover className="mb-0">
                                            <thead className="bg-light sticky-top">
                                                <tr>
                                                    <th className="ps-3">Página</th>
                                                    <th className="text-center">Ativos</th>
                                                    <th className="text-center">Views</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {realtimeData.pages.slice(0, 10).map((page, idx) => (
                                                    <tr key={idx}>
                                                        <td className="ps-3">
                                                            <small className="text-muted text-truncate d-block" style={{ maxWidth: '280px' }}>
                                                                {page.page}
                                                            </small>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="success">{page.activeUsers}</Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="secondary">{page.views}</Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="text-center py-4 text-muted">
                                            Nenhuma página sendo acessada
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white py-3">
                                    <h6 className="mb-0">⚡ Eventos Recentes</h6>
                                </Card.Header>
                                <Card.Body className="p-0" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                    {realtimeData?.events?.length > 0 ? (
                                        <Table hover className="mb-0">
                                            <thead className="bg-light sticky-top">
                                                <tr>
                                                    <th className="ps-3">Evento</th>
                                                    <th className="text-center">Contagem</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {realtimeData.events.slice(0, 10).map((event, idx) => (
                                                    <tr key={idx}>
                                                        <td className="ps-3">
                                                            <Badge bg={getEventColor(event.name)} className="me-2">
                                                                {getEventIcon(event.name)}
                                                            </Badge>
                                                            {translateEvent(event.name)}
                                                        </td>
                                                        <td className="text-center">
                                                            <strong>{event.count}</strong>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="text-center py-4 text-muted">
                                            Nenhum evento registrado
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                {/* TAB 3: PRODUTOS MAIS VISTOS */}
                <Tab eventKey="products" title="🛍️ Produtos">
                    <Row className="g-4 mt-2">
                        <Col md={12}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">🔥 Produtos Mais Visualizados (últimos {selectedPeriod} dias)</h6>
                                    <Badge bg="info">{topProducts?.length || 0} produtos</Badge>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {topProducts?.length > 0 ? (
                                        <Table hover className="mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-3" style={{ width: '50px' }}>#</th>
                                                    <th>Produto</th>
                                                    <th className="text-center">Visualizações</th>
                                                    <th className="text-center">Add to Cart</th>
                                                    <th className="text-center">Compras</th>
                                                    <th className="text-center">Conversão</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {topProducts.slice(0, 20).map((product, idx) => (
                                                    <tr key={idx}>
                                                        <td className="ps-3">
                                                            <Badge
                                                                bg={idx < 3 ? 'warning' : 'secondary'}
                                                                className="text-dark"
                                                            >
                                                                {idx + 1}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                {product.imagem && (
                                                                    <img
                                                                        src={product.imagem}
                                                                        alt=""
                                                                        style={{
                                                                            width: '40px',
                                                                            height: '40px',
                                                                            objectFit: 'cover',
                                                                            borderRadius: '4px',
                                                                            marginRight: '10px'
                                                                        }}
                                                                    />
                                                                )}
                                                                <div>
                                                                    <strong>{product.name}</strong>
                                                                    {product.id && (
                                                                        <br />
                                                                    )}
                                                                    <small className="text-muted">{product.id}</small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="primary" className="fs-6">
                                                                {product.views?.toLocaleString()}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="warning" className="text-dark">
                                                                {product.addedToCart || 0}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="success">
                                                                {product.purchased || 0}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={
                                                                product.conversionRate > 5 ? 'text-success fw-bold' :
                                                                    product.conversionRate > 2 ? 'text-warning' : 'text-muted'
                                                            }>
                                                                {product.conversionRate || 0}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <div className="fs-1 mb-2">🛍️</div>
                                            Nenhum dado de produto disponível
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                {/* TAB 4: DISPOSITIVOS */}
                <Tab eventKey="ads" title="💰 Google Ads">
                    <PainelGoogleAds />
                </Tab>

                <Tab eventKey="devices" title="📱 Dispositivos">
                    <Row className="g-4 mt-2">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white py-3">
                                    <h6 className="mb-0">📱 Usuários por Dispositivo (Tempo Real)</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <Card className="text-center h-100 border-info">
                                                <Card.Body>
                                                    <div className="fs-1 mb-2">📱</div>
                                                    <h3 className="text-info">{realtimeData?.devices?.mobile || 0}</h3>
                                                    <small className="text-muted">Mobile</small>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="text-center h-100 border-success">
                                                <Card.Body>
                                                    <div className="fs-1 mb-2">💻</div>
                                                    <h3 className="text-success">{realtimeData?.devices?.desktop || 0}</h3>
                                                    <small className="text-muted">Desktop</small>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="text-center h-100 border-warning">
                                                <Card.Body>
                                                    <div className="fs-1 mb-2">📟</div>
                                                    <h3 className="text-warning">{realtimeData?.devices?.tablet || 0}</h3>
                                                    <small className="text-muted">Tablet</small>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white py-3">
                                    <h6 className="mb-0">📊 Métricas Gerais ({selectedPeriod} dias)</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Table borderless>
                                        <tbody>
                                            <tr>
                                                <td>👥 Total de Usuários</td>
                                                <td className="text-end fw-bold">{overviewData?.totalUsers?.toLocaleString() || 0}</td>
                                            </tr>
                                            <tr>
                                                <td>🆕 Novos Usuários</td>
                                                <td className="text-end fw-bold text-success">{overviewData?.newUsers?.toLocaleString() || 0}</td>
                                            </tr>
                                            <tr>
                                                <td>📈 Sessões</td>
                                                <td className="text-end fw-bold">{overviewData?.sessions?.toLocaleString() || 0}</td>
                                            </tr>
                                            <tr>
                                                <td>📉 Taxa de Rejeição</td>
                                                <td className="text-end fw-bold text-warning">{(overviewData?.bounceRate * 100)?.toFixed(1) || 0}%</td>
                                            </tr>
                                            <tr>
                                                <td>👁️ Visualizações</td>
                                                <td className="text-end fw-bold">{overviewData?.pageViews?.toLocaleString() || 0}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>
            </Tabs>
        </div>
    );
};

export default AnalyticsDashboard;
